import React, { useMemo, useState } from "react";
import { toast } from "react-toastify";

const DAY_OPTIONS = ["MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY"];

const DAY_LABELS = {
    MONDAY: "E Hënë",
    TUESDAY: "E Martë",
    WEDNESDAY: "E Mërkurë",
    THURSDAY: "E Enjte",
    FRIDAY: "E Premte",
    SATURDAY: "E Shtunë",
};

const PLAN_CONFIG = {
    "3": {
        label: "3x Javë",
        requiredDays: 3,
        durations: {
            "1": { label: "1 Muaj", totalEntries: 12, amount: 60 },
            "3": { label: "3 Muaj", totalEntries: 36, amount: 160 },
        },
    },
    "5": {
        label: "5x Javë",
        requiredDays: 5,
        durations: {
            "1": { label: "1 Muaj", totalEntries: 20, amount: 80 },
            "3": { label: "3 Muaj", totalEntries: 60, amount: 210 },
        },
    },
};

const TIME_SLOTS = [
    { value: "08:00", label: "08:00 - 09:00" },
    { value: "18:00", label: "18:00 - 19:00" },
    { value: "19:00", label: "19:00 - 20:00" },
];

const getSortedSlots = (slots) => {
    const order = DAY_OPTIONS.reduce((acc, day, idx) => {
        acc[day] = idx;
        return acc;
    }, {});
    return [...slots].sort((a, b) => order[a.day] - order[b.day]);
};

export const AdultsFitnessDiscountCard = ({ setFormData }) => {
    const [planKey, setPlanKey] = useState("3");
    const [durationKey, setDurationKey] = useState("1");
    const [selectedSlots, setSelectedSlots] = useState([]);
    const [selected, setSelected] = useState(false);

    const selectedPlan = useMemo(() => PLAN_CONFIG[planKey], [planKey]);
    const selectedDuration = useMemo(
        () => PLAN_CONFIG[planKey].durations[durationKey],
        [planKey, durationKey]
    );

    const onPlanChange = (nextPlanKey) => {
        const nextPlan = PLAN_CONFIG[nextPlanKey];
        const trimmed = getSortedSlots(selectedSlots).slice(0, nextPlan.requiredDays);
        setPlanKey(nextPlanKey);
        setSelectedSlots(trimmed);
        setSelected(false);
    };

    const onDurationChange = (nextDurationKey) => {
        setDurationKey(nextDurationKey);
        setSelected(false);
    };

    const toggleDay = (day) => {
        const exists = selectedSlots.some((slot) => slot.day === day);
        if (exists) {
            setSelectedSlots(selectedSlots.filter((slot) => slot.day !== day));
            setSelected(false);
            return;
        }

        if (selectedSlots.length >= selectedPlan.requiredDays) {
            toast.warning(`Mund të zgjidhni maksimum ${selectedPlan.requiredDays} ditë për këtë plan.`);
            return;
        }

        setSelectedSlots([...selectedSlots, { day, time: "08:00" }]);
        setSelected(false);
    };

    const changeTime = (day, time) => {
        setSelectedSlots(
            selectedSlots.map((slot) => (slot.day === day ? { ...slot, time } : slot))
        );
        setSelected(false);
    };

    const handleChoosePlan = () => {
        if (selectedSlots.length !== selectedPlan.requiredDays) {
            toast.warning(`Ju lutem zgjidhni saktësisht ${selectedPlan.requiredDays} ditë.`);
            return;
        }

        const days = getSortedSlots(selectedSlots);
        setFormData((prevData) => ({
            ...prevData,
            days,
            tempDays: days,
            time: Number(durationKey),
            timings: days.map((slot) => `${slot.day}:${slot.time}`).join(","),
            entries: selectedDuration.totalEntries,
            totalEntries: selectedDuration.totalEntries,
            remainingEntries: selectedDuration.totalEntries,
            totalAmount: selectedDuration.amount,
            payableAmount: selectedDuration.amount,
        }));
        setSelected(true);
    };

    return (
        <div className="flex flex-col gap-5 border-2 border-gray-200 rounded-2xl min-w-[320px] p-6 max-h-fit h-fit font-custom2 bg-white shadow-lg hover:shadow-xl transition-shadow duration-300">
            <div className="flex flex-col gap-2 rounded-xl w-full">
                <label className="text-blue-700 font-semibold">Plani</label>
                <select
                    value={planKey}
                    onChange={(e) => onPlanChange(e.target.value)}
                    className="text-blue-700 bg-blue-50 border border-blue-200 font-semibold rounded-lg text-sm px-5 py-2.5"
                >
                    <option value="3">{PLAN_CONFIG["3"].label}</option>
                    <option value="5">{PLAN_CONFIG["5"].label}</option>
                </select>
            </div>

            <div className="flex flex-col gap-2 rounded-xl w-full">
                <label className="text-blue-700 font-semibold">Kohëzgjatja</label>
                <select
                    value={durationKey}
                    onChange={(e) => onDurationChange(e.target.value)}
                    className="text-blue-700 bg-blue-50 border border-blue-200 font-semibold rounded-lg text-sm px-5 py-2.5"
                >
                    <option value="1">{selectedPlan.durations["1"].label}</option>
                    <option value="3">{selectedPlan.durations["3"].label}</option>
                </select>
            </div>

            <div className="flex flex-col gap-2 rounded-xl w-full">
                <span className="text-blue-700 font-semibold">
                    Zgjidhni {selectedPlan.requiredDays} ditë
                </span>
                <div className="grid grid-cols-2 gap-2">
                    {DAY_OPTIONS.map((day) => {
                        const isSelected = selectedSlots.some((slot) => slot.day === day);
                        return (
                            <button
                                key={day}
                                type="button"
                                onClick={() => toggleDay(day)}
                                className={`border rounded px-3 py-2 text-sm ${
                                    isSelected
                                        ? "bg-blue-700 text-white border-blue-700"
                                        : "border-blue-700 text-blue-700"
                                }`}
                            >
                                {DAY_LABELS[day]}
                            </button>
                        );
                    })}
                </div>
            </div>

            {getSortedSlots(selectedSlots).length > 0 && (
                <div className="flex flex-col gap-2">
                    <span className="text-blue-700 font-semibold">Orari për secilën ditë</span>
                    {getSortedSlots(selectedSlots).map((slot) => (
                        <div key={slot.day} className="flex items-center justify-between gap-3">
                            <span className="text-sm font-semibold min-w-[110px]">{DAY_LABELS[slot.day]}</span>
                            <select
                                value={slot.time}
                                onChange={(e) => changeTime(slot.day, e.target.value)}
                                className="text-blue-700 bg-blue-50 border border-blue-200 font-semibold rounded-lg text-sm px-3 py-2"
                            >
                                {TIME_SLOTS.map((time) => (
                                    <option key={time.value} value={time.value}>
                                        {time.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    ))}
                </div>
            )}

            <div className="flex flex-col gap-2 bg-gray-50 rounded-lg p-4 border border-gray-200">
                <div className="flex items-center justify-between">
                    <span className="text-gray-700 font-semibold">Çmimi</span>
                    <span className="text-black-600 font-bold text-2xl">{selectedDuration.amount} €</span>
                </div>
            </div>

            <button
                onClick={handleChoosePlan}
                className={`min-w-full border-2 border-blue-600 font-bold rounded-lg py-3 px-6 uppercase tracking-wide shadow-md transition-all duration-300 ${selected ? "bg-blue-600 text-white" : "text-blue-600 hover:bg-blue-600 hover:text-white"}`}
            >
                Përzgjidh Planin
            </button>
        </div>
    );
};
